import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
    FaDesktop,
    FaLaptop,
    FaSync,
    FaShieldAlt,
    FaTimes,
    FaCog,
    FaNetworkWired,
    FaLock,
    FaHdd,
    FaMemory,
    FaMicrochip,
    FaDownload,
    FaTrash,
    FaInfoCircle,
    FaHeartbeat,
    FaBoxOpen,
    FaClipboardList,
    FaSearch,
    FaChevronLeft,
    FaChevronRight,
    FaUsers,
    FaIdBadge,
    FaBatteryHalf
} from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import * as Sentry from '@sentry/react';

const DeviceDetailModal = ({ isOpen, onClose, device, detail, health, loading, apiFetch, formatDate }) => {
    const [activeTab, setActiveTab] = useState('hardware');
    const [apps, setApps] = useState([]);
    const [appsTotalCount, setAppsTotalCount] = useState(0);
    const [appsSearch, setAppsSearch] = useState('');
    const [appsPage, setAppsPage] = useState(1);
    const [managedApps, setManagedApps] = useState([]);
    const [managedAppsSearch, setManagedAppsSearch] = useState('');
    const [actionLoading, setActionLoading] = useState({});

    const appSearchTimerRef = useRef(null);
    const managedAppSearchTimerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (appSearchTimerRef.current) clearTimeout(appSearchTimerRef.current);
            if (managedAppSearchTimerRef.current) clearTimeout(managedAppSearchTimerRef.current);
        };
    }, []);

    // Reset internal state when device changes
    useEffect(() => {
        if (isOpen) {
            setActiveTab('hardware');
            setApps([]);
            setAppsTotalCount(0);
            setAppsSearch('');
            setAppsPage(1);
            setManagedApps([]);
            setManagedAppsSearch('');
        }
    }, [isOpen, device?.deviceId]);

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '—';
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    const setActionState = (key, isLoading) => {
        setActionLoading(prev => ({ ...prev, [key]: isLoading }));
    };

    const loadDeviceApps = async (devId, search = '', pg = 1) => {
        try {
            const params = new URLSearchParams({ page: pg.toString(), pageSize: '50' });
            if (search) params.set('search', search);
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${devId}/apps?${params}`);
            if (res.ok) {
                const data = await res.json();
                setApps(data.apps || []);
                setAppsTotalCount(data.totalCount || 0);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const loadManagedApps = async (search = '') => {
        try {
            const params = new URLSearchParams({ page: '1', pageSize: '50' });
            if (search) params.set('search', search);
            const res = await apiFetch(`/api/UnifiedEndpoint/apps/managed?${params}`);
            if (res.ok) {
                const data = await res.json();
                setManagedApps(data.apps || []);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const handleInstallApp = async (appId, appName) => {
        if (!confirm(`Install "${appName}" on ${device?.deviceName}?`)) return;
        setActionState(`install-${appId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${device.deviceId}/install-app`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ appId })
            });
            const data = await res.json();
            if (data.success) toast.success(data.message);
            else toast.error(data.message || 'Install failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Install request failed.');
        } finally {
            setActionState(`install-${appId}`, false);
        }
    };

    const handleUninstallApp = async (appId, appName) => {
        if (!confirm(`Uninstall "${appName}" from ${device?.deviceName}?`)) return;
        setActionState(`uninstall-${appId}`, true);
        try {
            const res = await apiFetch(`/api/UnifiedEndpoint/devices/${device.deviceId}/uninstall-app`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ appId })
            });
            const data = await res.json();
            if (data.success) toast.success(data.message);
            else toast.error(data.message || 'Uninstall failed.');
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Uninstall request failed.');
        } finally {
            setActionState(`uninstall-${appId}`, false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="uem-modal-overlay" onClick={onClose}>
            <div className="uem-device-modal" onClick={e => e.stopPropagation()}>
                <div className="uem-device-modal-header">
                    <h2><FaLaptop /> Device Detail</h2>
                    <span className="uem-script-device-name">
                        <FaDesktop /> {device?.deviceName}
                    </span>
                    <button type="button" className="uem-modal-close" onClick={onClose} aria-label="Close"><FaTimes /></button>
                </div>

                {loading ? (
                    <div className="uem-detail-loading" style={{ padding: '60px' }}><FaSync className="uem-spin" /> Loading device details...</div>
                ) : (
                    <>
                        {/* Tabs */}
                        <div className="uem-script-categories">
                            {[
                                { id: 'hardware', label: 'Hardware', icon: <FaMicrochip /> },
                                { id: 'health', label: 'Health', icon: <FaHeartbeat /> },
                                { id: 'software', label: 'Software Audit', icon: <FaClipboardList /> },
                                { id: 'apps', label: 'App Management', icon: <FaBoxOpen /> },
                                { id: 'compliance', label: 'Compliance', icon: <FaShieldAlt /> }
                            ].map(tab => (
                                <button
                                    type="button"
                                    key={tab.id}
                                    className={`uem-script-cat-tab ${activeTab === tab.id ? 'uem-script-cat-tab--active' : ''}`}
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        if (tab.id === 'software' && apps.length === 0) loadDeviceApps(device.deviceId);
                                        if (tab.id === 'apps' && managedApps.length === 0) loadManagedApps();
                                    }}
                                >
                                    {tab.icon} {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="uem-device-modal-body">
                            {/* Hardware Tab */}
                            {activeTab === 'hardware' && detail && (
                                <div className="uem-dd-section">
                                    <div className="uem-dd-grid">
                                        <div className="uem-dd-card">
                                            <h4><FaDesktop /> System</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Device Name</span><span>{detail.deviceName}</span></div>
                                                <div className="uem-dd-row"><span>Manufacturer</span><span>{detail.manufacturer || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Model</span><span>{detail.model || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Serial Number</span><span>{detail.serialNumber || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Chassis</span><span>{detail.chassisType || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Category</span><span>{detail.deviceCategory || '—'}</span></div>
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaCog /> Operating System</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>OS</span><span>{detail.operatingSystem}</span></div>
                                                <div className="uem-dd-row"><span>Version</span><span>{detail.osVersion || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Build</span><span>{detail.osBuildNumber || '—'}</span></div>
                                                <div className="uem-dd-row"><span>SKU</span><span>{detail.skuFamily || '—'}</span></div>
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaMemory /> Hardware Specs</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Memory</span><span>{formatBytes(detail.physicalMemoryInBytes)}</span></div>
                                                <div className="uem-dd-row"><span>Total Storage</span><span>{formatBytes(detail.totalStorageSpaceInBytes)}</span></div>
                                                <div className="uem-dd-row"><span>Free Storage</span><span>{formatBytes(detail.freeStorageSpaceInBytes)}</span></div>
                                                <div className="uem-dd-row"><span>Processors</span><span>{detail.processorCount ?? '—'}</span></div>
                                                <div className="uem-dd-row"><span>Architecture</span><span>{detail.processorArchitecture || '—'}</span></div>
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaNetworkWired /> Network</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Wi-Fi MAC</span><span>{detail.wiFiMacAddress || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Ethernet MAC</span><span>{detail.ethernetMacAddress || '—'}</span></div>
                                                <div className="uem-dd-row"><span>IP Address</span><span>{detail.ipAddressV4 || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Subnet</span><span>{detail.subnetAddress || '—'}</span></div>
                                                {detail.fullyQualifiedDomainName && (
                                                    <div className="uem-dd-row">
                                                        <span title="Fully Qualified Domain Name">FQDN</span>
                                                        <span className="uem-dd-mono">{detail.fullyQualifiedDomainName}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaIdBadge /> Enrolment</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Enrolled</span><span>{formatDate(detail.enrolledDateTime)}</span></div>
                                                <div className="uem-dd-row"><span>Last Sync</span><span>{formatDate(detail.lastSyncDateTime)}</span></div>
                                                <div className="uem-dd-row"><span>Mgmt Agent</span><span>{detail.managementAgent || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Owner Type</span><span>{detail.managedDeviceOwnerType || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Azure AD ID</span><span className="uem-dd-mono">{detail.azureAdDeviceId || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Cert Expiry</span><span>{formatDate(detail.managementCertificateExpirationDate)}</span></div>
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaUsers /> User</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>User</span><span>{detail.userDisplayName || '—'}</span></div>
                                                <div className="uem-dd-row"><span>UPN</span><span>{detail.userPrincipalName || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Email</span><span>{detail.emailAddress || '—'}</span></div>
                                                <div className="uem-dd-row"><span>Phone</span><span>{detail.phoneNumber || '—'}</span></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Health Tab */}
                            {activeTab === 'health' && health && (
                                <div className="uem-dd-section">
                                    <div className="uem-dd-health-cards">
                                        <div className={`uem-dd-health-card ${(health.storageUsedPercent ?? 0) > 90 ? 'uem-dd-health-card--danger' : (health.storageUsedPercent ?? 0) > 75 ? 'uem-dd-health-card--warning' : 'uem-dd-health-card--ok'}`}>
                                            <FaHdd className="uem-dd-health-icon" />
                                            <div className="uem-dd-health-metric">
                                                <span className="uem-dd-health-value">{health.storageUsedPercent ?? '—'}%</span>
                                                <span className="uem-dd-health-label">Storage Used</span>
                                            </div>
                                            <div className="uem-dd-health-bar">
                                                <div className="uem-dd-health-bar-fill" style={{ width: `${health.storageUsedPercent ?? 0}%` }} />
                                            </div>
                                            <span className="uem-dd-health-sub">{formatBytes(health.freeStorageBytes)} free of {formatBytes(health.totalStorageBytes)}</span>
                                        </div>
                                        <div className="uem-dd-health-card uem-dd-health-card--ok">
                                            <FaMemory className="uem-dd-health-icon" />
                                            <div className="uem-dd-health-metric">
                                                <span className="uem-dd-health-value">{formatBytes(health.physicalMemoryBytes)}</span>
                                                <span className="uem-dd-health-label">Physical Memory</span>
                                            </div>
                                        </div>
                                        <div className={`uem-dd-health-card ${health.complianceState?.toLowerCase() === 'compliant' ? 'uem-dd-health-card--ok' : 'uem-dd-health-card--danger'}`}>
                                            <FaShieldAlt className="uem-dd-health-icon" />
                                            <div className="uem-dd-health-metric">
                                                <span className="uem-dd-health-value">{health.complianceState || '—'}</span>
                                                <span className="uem-dd-health-label">Compliance</span>
                                            </div>
                                        </div>
                                        <div className={`uem-dd-health-card ${health.isEncrypted ? 'uem-dd-health-card--ok' : 'uem-dd-health-card--danger'}`}>
                                            <FaLock className="uem-dd-health-icon" />
                                            <div className="uem-dd-health-metric">
                                                <span className="uem-dd-health-value">{health.isEncrypted ? 'Encrypted' : 'Not Encrypted'}</span>
                                                <span className="uem-dd-health-label">Disk Encryption</span>
                                            </div>
                                        </div>
                                        {/* Battery card — only shown when battery data is available (laptops/mobile) */}
                                        {health.batteryChargePercent != null && (
                                            (() => {
                                                // Extract charge percentage once to avoid repetition in the class expression
                                                const pct = health.batteryChargePercent;
                                                const batteryClass = pct < 20
                                                    ? 'uem-dd-health-card--danger'
                                                    : pct < 50
                                                        ? 'uem-dd-health-card--warning'
                                                        : 'uem-dd-health-card--ok';
                                                return (
                                                    <div className={`uem-dd-health-card ${batteryClass}`}>
                                                        <FaBatteryHalf className="uem-dd-health-icon" />
                                                        <div className="uem-dd-health-metric">
                                                            <span className="uem-dd-health-value">{pct}%</span>
                                                            <span className="uem-dd-health-label">Battery Health</span>
                                                        </div>
                                                        {health.batteryChargeCycles != null && (
                                                            <span className="uem-dd-health-sub">{health.batteryChargeCycles} charge cycles</span>
                                                        )}
                                                    </div>
                                                );
                                            })()
                                        )}
                                    </div>

                                    <div className="uem-dd-grid" style={{ marginTop: '16px' }}>
                                        <div className="uem-dd-card">
                                            <h4><FaShieldAlt /> Windows Defender</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Protection Enabled</span><span>{health.windowsProtectionEnabled ? '✓ Yes' : '✗ No'}</span></div>
                                                <div className="uem-dd-row"><span>Real-Time Protection</span><span>{health.realTimeProtectionEnabled ? '✓ Yes' : '✗ No'}</span></div>
                                                <div className="uem-dd-row"><span>Definitions Up-to-Date</span><span>{health.isDefenderUpToDate ? '✓ Yes' : '✗ No'}</span></div>
                                                <div className="uem-dd-row"><span>Jailbroken</span><span>{health.jailBroken || '—'}</span></div>
                                            </div>
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaInfoCircle /> Status</h4>
                                            <div className="uem-dd-rows">
                                                <div className="uem-dd-row"><span>Last Sync</span><span>{formatDate(health.lastSyncDateTime)}</span></div>
                                                <div className="uem-dd-row"><span>Mgmt Cert Expiry</span><span>{formatDate(health.managementCertExpiry)}</span></div>
                                                {health.certExpiringSoon && (
                                                    <div className="uem-dd-row uem-dd-row--warn"><span>⚠ Certificate expiring within 30 days!</span></div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Software Audit Tab */}
                            {activeTab === 'software' && (
                                <div className="uem-dd-section">
                                    <div className="uem-dd-toolbar">
                                        <div className="uem-search-wrapper" style={{ maxWidth: '400px' }}>
                                            <FaSearch className="uem-search-icon" />
                                            <input
                                                type="text"
                                                className="uem-search-input"
                                                placeholder="Search installed software..."
                                                value={appsSearch}
                                                onChange={e => {
                                                    setAppsSearch(e.target.value);
                                                    if (appSearchTimerRef.current) clearTimeout(appSearchTimerRef.current);
                                                    appSearchTimerRef.current = setTimeout(() => {
                                                        loadDeviceApps(device.deviceId, e.target.value, 1);
                                                        setAppsPage(1);
                                                    }, 400);
                                                }}
                                            />
                                        </div>
                                        <span className="uem-stat"><FaClipboardList /> {appsTotalCount} apps detected</span>
                                    </div>
                                    <div className="uem-dd-app-table-wrap">
                                        <table className="uem-dd-app-table">
                                            <thead>
                                                <tr>
                                                    <th>Application</th>
                                                    <th>Version</th>
                                                    <th>Publisher</th>
                                                    <th>Size</th>
                                                    <th style={{ width: '50px', textAlign: 'center' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {apps.length === 0 ? (
                                                    <tr><td colSpan="5" className="uem-empty">No apps found.</td></tr>
                                                ) : apps.map((app, i) => (
                                                    <tr key={app.appId || i}>
                                                        <td className="uem-dd-app-name">{app.displayName}</td>
                                                        <td>{app.version || '—'}</td>
                                                        <td>{app.publisher || '—'}</td>
                                                        <td>{formatBytes(app.sizeInByte)}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="uem-device-action-btn uem-device-action-btn--danger uem-dd-uninstall-btn"
                                                                title={`Uninstall ${app.displayName}`}
                                                                aria-label={`Uninstall ${app.displayName}`}
                                                                disabled={actionLoading[`uninstall-${app.appId}`]}
                                                                onClick={() => handleUninstallApp(app.appId, app.displayName)}
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {appsTotalCount > 50 && (
                                        <div className="uem-pagination" style={{ paddingTop: '8px' }}>
                                            <button type="button" className="uem-page-btn" disabled={appsPage <= 1}
                                                onClick={() => { const p = appsPage - 1; setAppsPage(p); loadDeviceApps(device.deviceId, appsSearch, p); }}>
                                                <FaChevronLeft /> Prev
                                            </button>
                                            <span className="uem-page-info">Page {appsPage}</span>
                                            <button type="button" className="uem-page-btn" disabled={apps.length < 50}
                                                onClick={() => { const p = appsPage + 1; setAppsPage(p); loadDeviceApps(device.deviceId, appsSearch, p); }}>
                                                Next <FaChevronRight />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* App Management Tab */}
                            {activeTab === 'apps' && (
                                <div className="uem-dd-section">
                                    <div className="uem-dd-toolbar">
                                        <div className="uem-search-wrapper" style={{ maxWidth: '400px' }}>
                                            <FaSearch className="uem-search-icon" />
                                            <input
                                                type="text"
                                                className="uem-search-input"
                                                placeholder="Search available apps..."
                                                value={managedAppsSearch}
                                                onChange={e => {
                                                    setManagedAppsSearch(e.target.value);
                                                    if (managedAppSearchTimerRef.current) clearTimeout(managedAppSearchTimerRef.current);
                                                    managedAppSearchTimerRef.current = setTimeout(() => {
                                                        loadManagedApps(e.target.value);
                                                    }, 400);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="uem-dd-apps-grid">
                                        {managedApps.length === 0 ? (
                                            <p className="uem-no-data">No managed apps found. Search to find apps in your Intune catalog.</p>
                                        ) : managedApps.map(app => (
                                            <div key={app.appId} className="uem-dd-managed-app-card">
                                                <div className="uem-dd-managed-app-info">
                                                    <h4>{app.displayName}</h4>
                                                    <span className="uem-dd-app-publisher">{app.publisher || 'Unknown publisher'}</span>
                                                    {app.appType && <span className="uem-script-meta-tag">{app.appType}</span>}
                                                </div>
                                                <div className="uem-dd-managed-app-actions">
                                                    <button
                                                        type="button"
                                                        className="uem-device-action-btn uem-device-action-btn--connect"
                                                        disabled={actionLoading[`install-${app.appId}`]}
                                                        onClick={() => handleInstallApp(app.appId, app.displayName)}
                                                        aria-label={`Install ${app.displayName}`}
                                                    >
                                                        <FaDownload /> Install
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="uem-device-action-btn uem-device-action-btn--danger"
                                                        disabled={actionLoading[`uninstall-${app.appId}`]}
                                                        onClick={() => handleUninstallApp(app.appId, app.displayName)}
                                                        aria-label={`Uninstall ${app.displayName}`}
                                                    >
                                                        <FaTrash /> Uninstall
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Compliance Tab */}
                            {activeTab === 'compliance' && detail && (
                                <div className="uem-dd-section">
                                    {/* Security Status */}
                                    <div className="uem-dd-card" style={{ marginBottom: '16px' }}>
                                        <h4><FaLock /> Security Status</h4>
                                        <div className="uem-dd-rows">
                                            <div className="uem-dd-row"><span>Overall Compliance</span>
                                                <span className={`uem-badge ${detail.complianceState?.toLowerCase() === 'compliant' ? 'uem-badge--success' : 'uem-badge--danger'}`}>
                                                    {detail.complianceState || 'Unknown'}
                                                </span>
                                            </div>
                                            <div className="uem-dd-row"><span>Encrypted</span><span>{detail.isEncrypted ? '✓ Yes' : '✗ No'}</span></div>
                                            <div className="uem-dd-row"><span>Jailbroken</span><span>{detail.jailBroken || '—'}</span></div>
                                            <div className="uem-dd-row"><span>Threat Level</span><span>{detail.deviceThreatLevel || '—'}</span></div>
                                            <div className="uem-dd-row"><span>Azure AD Registered</span><span>{detail.azureAdRegistered ? '✓ Yes' : '✗ No'}</span></div>
                                            <div className="uem-dd-row"><span>Supervised</span><span>{detail.isSupervised ? '✓ Yes' : '✗ No'}</span></div>
                                        </div>
                                    </div>

                                    {/* Device-specific policy states */}
                                    <div className="uem-dd-grid">
                                        <div className="uem-dd-card">
                                            <h4><FaShieldAlt /> Device Compliance States</h4>
                                            {(detail.compliancePolicies?.length > 0) ? (
                                                <div className="uem-dd-rows">
                                                    {detail.compliancePolicies.map((p, i) => (
                                                        <div key={i} className="uem-dd-row">
                                                            <span>{p.policyName}</span>
                                                            <div className="uem-dd-row-right">
                                                                <span className={`uem-badge ${p.state?.toLowerCase() === 'compliant' ? 'uem-badge--success' : p.state?.toLowerCase() === 'noncompliant' || p.state?.toLowerCase() === 'nonCompliant' ? 'uem-badge--danger' : 'uem-badge--warning'}`}>
                                                                    {p.state}
                                                                </span>
                                                                {p.lastReportedDateTime && <span className="uem-dd-sub-date">{formatDate(p.lastReportedDateTime)}</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : <p className="uem-no-data">No compliance policy states reported for this device.</p>}
                                        </div>
                                        <div className="uem-dd-card">
                                            <h4><FaCog /> Device Configuration States</h4>
                                            {(detail.configurationProfiles?.length > 0) ? (
                                                <div className="uem-dd-rows">
                                                    {detail.configurationProfiles.map((p, i) => (
                                                        <div key={i} className="uem-dd-row">
                                                            <span>{p.profileName}</span>
                                                            <div className="uem-dd-row-right">
                                                                <span className={`uem-badge ${p.state?.toLowerCase() === 'compliant' ? 'uem-badge--success' : p.state?.toLowerCase() === 'noncompliant' || p.state?.toLowerCase() === 'nonCompliant' ? 'uem-badge--danger' : 'uem-badge--warning'}`}>
                                                                    {p.state}
                                                                </span>
                                                                {p.lastReportedDateTime && <span className="uem-dd-sub-date">{formatDate(p.lastReportedDateTime)}</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : <p className="uem-no-data">No configuration profile states reported for this device.</p>}
                                        </div>
                                    </div>

                                    {/* Tenant-wide policies & profiles management */}
                                    <div className="uem-dd-card" style={{ marginTop: '16px' }}>
                                        <h4><FaShieldAlt /> Tenant Compliance Policies</h4>
                                        {(detail.allCompliancePolicies?.length > 0) ? (
                                            <div className="uem-dd-app-table-wrap">
                                                <table className="uem-dd-app-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Policy Name</th>
                                                            <th>Description</th>
                                                            <th>Last Modified</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detail.allCompliancePolicies.map((p, i) => (
                                                            <tr key={i}>
                                                                <td className="uem-dd-app-name">{p.displayName}</td>
                                                                <td>{p.description || '—'}</td>
                                                                <td>{formatDate(p.lastModifiedDateTime)}</td>
                                                                <td>
                                                                    <a
                                                                        className="uem-device-action-btn uem-device-action-btn--info uem-dd-manage-link"
                                                                        href="https://intune.microsoft.com/#view/Microsoft_Intune_DeviceSettings/DeviceComplianceMenu/~/policies"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Manage in Intune"
                                                                        aria-label="Manage compliance policy in Intune"
                                                                    >
                                                                        <FiEdit2 />
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : <p className="uem-no-data">No compliance policies found in tenant.</p>}
                                    </div>

                                    <div className="uem-dd-card" style={{ marginTop: '16px' }}>
                                        <h4><FaCog /> Tenant Configuration Profiles</h4>
                                        {(detail.allConfigProfiles?.length > 0) ? (
                                            <div className="uem-dd-app-table-wrap">
                                                <table className="uem-dd-app-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Profile Name</th>
                                                            <th>Description</th>
                                                            <th>Last Modified</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detail.allConfigProfiles.map((p, i) => (
                                                            <tr key={i}>
                                                                <td className="uem-dd-app-name">{p.displayName}</td>
                                                                <td>{p.description || '—'}</td>
                                                                <td>{formatDate(p.lastModifiedDateTime)}</td>
                                                                <td>
                                                                    <a
                                                                        className="uem-device-action-btn uem-device-action-btn--info uem-dd-manage-link"
                                                                        href="https://intune.microsoft.com/#view/Microsoft_Intune_DeviceSettings/DevicesMenu/~/configurationProfiles"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Manage in Intune"
                                                                        aria-label="Manage configuration profile in Intune"
                                                                    >
                                                                        <FiEdit2 />
                                                                    </a>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : <p className="uem-no-data">No configuration profiles found in tenant.</p>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DeviceDetailModal;
